import { http, If, location, onMount, reactive } from 'cdui-js';
import { fetchBusConfigs, uploadFile } from '../../../api/common';
import { fetchStockAccountsMeta } from '../../../api/funds';
import { CurrencyFlag } from '../../../components/CurrencyFlag';
import i18n from '../../../i18n';
import { aesEncryptHexByPass } from '../../../lib/crypto';
import { initCountry } from '../../../stores/bus-config';
import { commonState, loadSysConfigurations, waitForEncryptKey } from '../../../stores/common';
import { getUserInfo, userState } from '../../../stores/user';
import { SelectBox } from '../components/SelectBox';
import { SelectCountry } from '../components/SelectCountry';
import { showModal } from '../components/Modal';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { CodeSheet, type CodeConfirm } from './CodeSheet';
import { bankVerifyText } from './verifyCopy';
import { BankShell } from './Shell';
import { DiFiInput } from '../../../components/DiFi/DiFiInput';
import { DiFiUpload } from '../../../components/DiFi/DiFiUpload';
import { PROOF_IMAGE_MAX_BYTES } from '../components/Upload';
import {
  errorMessage,
  FALLBACK_ACCOUNT_TYPES,
  fetchMarketBanks,
  isBizCode,
  listUrl,
  mapAccountTypes,
  mapBankOptions,
  openAddSuccess,
  pickSubUserId,
  resolveBankName,
  setMarketBankCache,
  shouldLeaveForm,
  unwrapList,
  type DictOption,
} from './shared';
import './bank.css';

const IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png';

const filePathOf = (data: any) => {
  if (typeof data?.filePath === 'string') return data.filePath.trim();
  if (typeof data?.data === 'string') return data.data.trim();
  if (typeof data?.data?.filePath === 'string') return data.data.filePath.trim();
  return '';
};

const currencyChips = () => [
  { value: 'HKD', label: i18n.BankAccount.CurrencyHkd },
  { value: 'USD', label: i18n.BankAccount.CurrencyUsd },
  { value: 'CNY', label: i18n.BankAccount.CurrencyCny },
];

const emptyErrors = () => ({
  bankAccountName: '',
  bankCountry: '',
  bankCode: '',
  bankAccountNo: '',
  bankAccountType: '',
  currencies: '',
  proof: '',
});

export const BankAccountAdd = () => {
  const state = reactive({
    prefillReady: false,
    blocked: false,
    bankAccountName: '',
    bankCountry: '',
    bankCode: '',
    bankAccountNo: '',
    bankAccountType: '',
    currencies: [] as string[],
    proofs: [] as string[],
    banks: [] as DictOption[],
    accountTypes: FALLBACK_ACCOUNT_TYPES() as DictOption[],
    rawBanks: [] as any[],
    subUserId: '',
    uploading: false,
    busyFiles: [] as File[],
    submitting: false,
    codeOpen: false,
    codeError: '',
    errors: emptyErrors(),
  });

  const bankLabel = () => state.banks.find((item) => item.value === state.bankCode)?.label || '';
  const uploadedPath = new Map<File, string>();
  let pickedFiles: File[] = [];
  let uploadJobs = 0;

  const applyProofFiles = (files: File[]) => {
    const keep = new Set(files);
    for (const file of [...uploadedPath.keys()]) {
      if (!keep.has(file)) uploadedPath.delete(file);
    }
    state.proofs = files.map((file) => uploadedPath.get(file)).filter((path): path is string => !!path);
    state.busyFiles = state.busyFiles.filter((file) => files.includes(file));
    if (state.proofs.length) state.errors.proof = '';
  };

  const uploadIncoming = async (files: File[]) => {
    const pending = files.filter((file) => !uploadedPath.has(file));
    if (!pending.length) return;
    uploadJobs += 1;
    state.uploading = true;
    state.busyFiles = [...state.busyFiles, ...pending.filter((file) => state.busyFiles.indexOf(file) < 0)];
    try {
      for (const file of pending) {
        try {
          const data = await uploadFile({
            file,
            useFor: 'bank_account_prove',
            busId: String(userState.id || '0'),
          });
          const path = filePathOf(data);
          if (!path || !pickedFiles.includes(file)) continue;
          uploadedPath.set(file, path);
        } catch {
          // 业务错误由请求拦截器提示
        } finally {
          state.busyFiles = state.busyFiles.filter((item) => item !== file);
        }
      }
    } finally {
      uploadJobs -= 1;
      state.uploading = uploadJobs > 0;
      applyProofFiles(pickedFiles);
    }
  };

  onMount(() => {
    if (shouldLeaveForm()) {
      location.routeTo(listUrl());
      return;
    }
    void (async () => {
      try {
        await bootDepositApp();
      } catch {
        // 浏览器调试继续用本地 token
      }
      try {
        await getUserInfo();
      } catch {
        // 没有用户资料时上传 busId 用 0
      }
      void loadPrefill();
      void loadDicts();
      void loadSubUser();
    })();
  });

  const loadPrefill = async () => {
    try {
      await http.get('/user/bankAddApply/prefill');
      state.prefillReady = true;
    } catch (error) {
      state.blocked = true;
      if (isBizCode(error, '124001')) {
        showModal({
          title: i18n.FundH5.Tip,
          content: () => i18n.BankList.NeedRealName,
          confirmText: i18n.FundH5.IKnow,
        });
      }
    }
  };

  const applyBanks = () => {
    state.banks = mapBankOptions(state.rawBanks);
  };

  const loadDicts = async () => {
    // 「银行所在国家/地区」选项来自 GET /sys/dict/country
    const countryTask = initCountry().catch(() => {});
    try {
      const configs = (await fetchBusConfigs()) as any;
      state.rawBanks = Array.isArray(configs?.hongKongBank) ? configs.hongKongBank : [];
      state.accountTypes = mapAccountTypes(configs?.bankAccountType || []);
      applyBanks();
    } catch {
      state.accountTypes = FALLBACK_ACCOUNT_TYPES();
    }
    try {
      setMarketBankCache(unwrapList(await fetchMarketBanks()));
      applyBanks();
    } catch {
      // 银行名用业务配置里的文案
    }
    await countryTask;
  };

  const loadSubUser = async () => {
    try {
      state.subUserId = pickSubUserId(await fetchStockAccountsMeta());
    } catch {
      state.subUserId = '';
    }
  };

  const validate = () => {
    const errors = emptyErrors();
    if (!state.bankAccountName.trim()) errors.bankAccountName = i18n.BankList.ErrHolderName;
    if (!state.bankCountry) errors.bankCountry = i18n.BankList.ErrCountry;
    if (!state.bankCode) errors.bankCode = i18n.BankList.ErrBankName;
    if (!state.bankAccountNo.trim()) errors.bankAccountNo = i18n.BankList.ErrAccountNo;
    if (!state.bankAccountType) errors.bankAccountType = i18n.BankList.ErrAccountType;
    if (state.bankAccountType === 'INTEGRATED' && !state.currencies.length) errors.currencies = i18n.BankList.ErrCurrencies;
    if (!state.proofs.length) errors.proof = i18n.BankList.ErrProof;
    state.errors = errors;
    return !Object.values(errors).some(Boolean);
  };

  const openCode = () => {
    if (state.blocked || !state.prefillReady) return;
    if (!validate()) return;
    state.codeError = '';
    state.codeOpen = true;
  };

  const toggleCurrency = (code: string) => {
    state.currencies = state.currencies.includes(code)
      ? state.currencies.filter((item) => item !== code)
      : [...state.currencies, code];
    state.errors.currencies = '';
  };

  const submit = async (payload: CodeConfirm) => {
    if (state.submitting || state.blocked) return;
    state.submitting = true;
    state.codeError = '';
    try {
      let pass = await waitForEncryptKey();
      if (!pass) {
        await loadSysConfigurations();
        pass = commonState.encryptKey;
      }
      if (!pass) {
        state.codeError = bankVerifyText().encryptFail;
        return;
      }
      let cipher = '';
      try {
        cipher = aesEncryptHexByPass(state.bankAccountNo.replace(/\s/g, ''), pass);
      } catch {
        cipher = '';
      }
      if (!cipher) {
        state.codeError = bankVerifyText().encryptFail;
        return;
      }
      const bankName = resolveBankName({ bankCode: state.bankCode, bankName: bankLabel() }, bankLabel());
      const body: Record<string, unknown> = {
        bankCountry: state.bankCountry,
        bankCode: state.bankCode,
        bankName,
        bankAccountName: state.bankAccountName.trim(),
        bankAccountNo: cipher,
        bankAccountType: state.bankAccountType,
        currencies: state.bankAccountType === 'INTEGRATED' ? state.currencies.join(',') : '',
        accountProofFilePaths: state.proofs.join(','),
        verifyCode: payload.verifyCode,
        validType: payload.validType,
      };
      if (state.subUserId) body.subUserId = state.subUserId;
      await http.post('/user/bankAddApply/apply/submit', body, { headers: { 'X-No-Toast': '1' } });
      state.codeOpen = false;
      openAddSuccess();
    } catch (error) {
      state.codeError = errorMessage(error, bankVerifyText().submitFail);
    } finally {
      state.submitting = false;
    }
  };

  const canSubmit = () => state.prefillReady && !state.blocked && !state.submitting;

  return (
    <BankShell
      title={i18n.BankAccount.AddBank}
      footer={
        <button type="button" class="btn-primary" disabled={!canSubmit() || state.uploading} onclick={openCode}>
          {i18n.FundH5.Submit}
        </button>
      }
      overlay={
        <If when={state.codeOpen}>
          <CodeSheet
            error={state.codeError}
            submitting={state.submitting}
            onClose={() => {
              if (state.submitting) return;
              state.codeError = '';
              state.codeOpen = false;
            }}
            onEdit={() => {
              state.codeError = '';
            }}
            onConfirm={(payload) => {
              void submit(payload);
            }}
          />
        </If>
      }
    >
      <div class="form">
        <div class="form-section">
          <div class="form-section-title">{i18n.BankAccount.BankInfo}</div>
          <div class={`field${state.errors.bankAccountName ? ' is-error' : ''}`}>
            <div class="field-label">{i18n.BankAccount.HolderName}</div>
            <DiFiInput
              shellClass="f-input-2"
              placeholder={i18n.BankList.PhName}
              value={state.bankAccountName}
              onUpdateValue={(v) => {
                state.bankAccountName = v;
                state.errors.bankAccountName = '';
              }}
            />
            {state.errors.bankAccountName ? <div class="err">{state.errors.bankAccountName}</div> : null}
          </div>
          <div class={`field${state.errors.bankCountry ? ' is-error' : ''}`}>
            <div class="field-label">{i18n.BankAccount.BankCountry}</div>
            <SelectCountry
              title={i18n.BankList.SelectCountry}
              placeholder={i18n.BankList.SelectCountry}
              value={state.bankCountry}
              change={(value) => {
                state.bankCountry = value;
                state.errors.bankCountry = '';
              }}
            />
            {state.errors.bankCountry ? <div class="err">{state.errors.bankCountry}</div> : null}
          </div>
          <div class={`field${state.errors.bankCode ? ' is-error' : ''}`}>
            <div class="field-label">{i18n.BankAccount.BankName}</div>
            <SelectBox
              title={i18n.BankList.SelectBank}
              placeholder={i18n.BankList.PhSelectBank}
              value={state.bankCode}
              options={state.banks}
              change={(value) => {
                state.bankCode = value;
                state.errors.bankCode = '';
              }}
            />
            {state.errors.bankCode ? <div class="err">{state.errors.bankCode}</div> : null}
          </div>
          <div class={`field${state.errors.bankAccountNo ? ' is-error' : ''}`}>
            <div class="field-label">{i18n.BankAccount.AccountNo}</div>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="bank-account"
              inputMode="numeric"
              placeholder={i18n.BankList.PhAccountNo}
              value={state.bankAccountNo}
              onUpdateValue={(v) => {
                state.bankAccountNo = v;
                state.errors.bankAccountNo = '';
              }}
            />
            {state.errors.bankAccountNo ? <div class="err">{state.errors.bankAccountNo}</div> : null}
          </div>
          <div class={`field${state.errors.bankAccountType ? ' is-error' : ''}`}>
            <div class="field-label">{i18n.BankList.LabelCurrencyKind}</div>
            <SelectBox
              title={i18n.BankList.SelectAccountType}
              placeholder={i18n.BankList.PhSelectAccountType}
              value={state.bankAccountType}
              options={state.accountTypes}
              change={(value) => {
                state.bankAccountType = value;
                state.currencies = [];
                state.errors.bankAccountType = '';
                state.errors.currencies = '';
              }}
            />
            {state.errors.bankAccountType ? <div class="err">{state.errors.bankAccountType}</div> : null}
          </div>
          <If when={state.bankAccountType === 'INTEGRATED'}>
            <div class="field">
              <div class="field-label">{i18n.BankAccount.CurrencyType}</div>
              <div class="currency-row">
                {currencyChips().map((item) => (
                  <button
                    type="button"
                    class={`currency-chip${state.currencies.includes(item.value) ? ' is-on' : ''}`}
                    onclick={() => toggleCurrency(item.value)}
                  >
                    <CurrencyFlag code={item.value} />
                    {item.label}
                  </button>
                ))}
              </div>
              {state.errors.currencies ? <div class="err">{state.errors.currencies}</div> : null}
            </div>
          </If>
        </div>

        <div class="form-section">
          <div class="form-section-title">{i18n.BankAccount.UploadProof}</div>
          <DiFiUpload
            accept={IMAGE_ACCEPT}
            multiple={true}
            maxCount={5}
            maxSize={PROOF_IMAGE_MAX_BYTES}
            width={64}
            height={64}
            listType="image"
            loadingFiles={state.busyFiles}
            onFilesUpdate={(files) => {
              pickedFiles = files;
              applyProofFiles(files);
            }}
            onChange={(payload) => {
              void uploadIncoming(payload.allFiles || []);
            }}
          />
          {state.errors.proof ? <div class="err">{state.errors.proof}</div> : null}
        </div>
      </div>
    </BankShell>
  );
};
